import Chip from '@mui/material/Chip';
import React from 'react';

import { useAppSelector } from '@/store';

import { DashboardCard } from './DashboardCard';

const EXPERT_STATE_LABELS: Record<
  string,
  { label: string; color: 'success' | 'warning' | 'info' | 'default' }
> = {
  active: { label: 'Active', color: 'success' },
  applying: { label: 'Applying', color: 'info' },
  rejected: { label: 'Not accepted', color: 'default' },
  badged: { label: 'Badged', color: 'success' },
  pending: { label: 'Pending review', color: 'warning' },
};

function StatIcon({ color, children }: { color: string; children: React.ReactNode }) {
  return (
    <div
      className={`flex h-[3.6rem] w-[3.6rem] shrink-0 items-center justify-center rounded-md ${color}`}
    >
      {children}
    </div>
  );
}

export function MyActivityCard(): React.ReactNode {
  const viewer = useAppSelector((state) => state.viewer);
  const profileEndpoint = viewer.profile?.url_endpoint;
  const expertState = viewer.expert_state ?? viewer.profile?.expert_state;
  const stateInfo = EXPERT_STATE_LABELS[expertState ?? ''];
  const profileUrl = profileEndpoint ? `/profile/${profileEndpoint}` : '/profile';

  const expertStats = (
    viewer as {
      expert_stats?: {
        consultation_count?: number;
        candidate_summary?: {
          matched_count?: number;
          verified_count?: number;
          vetting_count?: number;
        };
      };
    }
  ).expert_stats;
  const consultationCount = expertStats?.consultation_count ?? 0;
  const candidateSummary = expertStats?.candidate_summary;

  return (
    <DashboardCard title="My Activity" linkText="View full stats" linkTo={profileUrl}>
      <div className="flex flex-col gap-14">
        {stateInfo && (
          <div className="flex items-center justify-between">
            <span className="text-12 text-on-surface-muted">Expert status</span>
            <Chip
              label={stateInfo.label}
              color={stateInfo.color}
              size="small"
              variant="outlined"
              // MUI's success green is ~2:1 on the light chip fill; --darkGreen reads in both modes.
              sx={
                stateInfo.color === 'success'
                  ? { color: 'var(--darkGreen)', borderColor: 'var(--darkGreen)' }
                  : undefined
              }
            />
          </div>
        )}

        <div className="flex items-center gap-10">
          <StatIcon color="bg-brand-light">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="var(--brandPrimary)"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
          </StatIcon>
          <div>
            <span className="text-20 font-bold text-on-surface">{consultationCount}</span>
            <span className="ml-4 text-12 text-on-surface-muted">Consultations</span>
          </div>
        </div>

        {candidateSummary && (
          <div className="flex items-center gap-10">
            <StatIcon color="bg-green-500/15">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#059669"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
            </StatIcon>
            <div className="flex items-center gap-8 text-14">
              <span>
                <span className="font-semibold">{candidateSummary.vetting_count ?? 0}</span>
                <span className="ml-2 text-12 text-on-surface-muted">Vetting</span>
              </span>
              <span className="text-on-surface-subtle">|</span>
              <span>
                <span className="font-semibold">{candidateSummary.verified_count ?? 0}</span>
                <span className="ml-2 text-12 text-on-surface-muted">Verified</span>
              </span>
              <span className="text-on-surface-subtle">|</span>
              <span>
                <span className="font-semibold">{candidateSummary.matched_count ?? 0}</span>
                <span className="ml-2 text-12 text-on-surface-muted">Matched</span>
              </span>
            </div>
          </div>
        )}
      </div>
    </DashboardCard>
  );
}
